import { Star, ExternalLink } from "lucide-react";
import { reviews, reviewSummary, reviewPlatforms, type Review } from "@/data/reviews";
import trustooLogo from "@/assets/partner-trustoo.svg";
import verhuisoffertesLogo from "@/assets/partner-verhuisoffertes.svg";
import googleAsset from "@/assets/logo-google.png.asset.json";
import sireloAsset from "@/assets/logo-sirelo.png.asset.json";

/** Officiële logo's van de reviewplatformen. */
const platformLogos: Record<string, string> = {
  Trustoo: trustooLogo,
  VerhuisOffertes: verhuisoffertesLogo,
  Google: googleAsset.url,
  Sirelo: sireloAsset.url,
};

/** Nieuwste eerst, afwisselend per platform (Google, Trustoo, VerhuisOffertes...). */
export function mixBySource(list: Review[]): Review[] {
  const sorted = [...list].sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
  const groups = new Map<string, Review[]>();
  for (const r of sorted) groups.set(r.source, [...(groups.get(r.source) ?? []), r]);
  const queues = [...groups.values()];
  const out: Review[] = [];
  while (out.length < sorted.length) for (const q of queues) { const r = q.shift(); if (r) out.push(r); }
  return out;
}

export function Stars({ rating = 5 }: { rating?: number }) {
  return (
    <span className="flex gap-0.5" aria-label={`${rating} van de 5 sterren`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`size-4 ${i <= rating ? "fill-brand text-brand" : "text-border"}`}
          aria-hidden
        />
      ))}
    </span>
  );
}

export function ReviewPlatforms() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {reviewPlatforms.map((p) => (
        <a
          key={p.name}
          href={p.url}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-xl border border-border bg-background p-5 shadow-[var(--shadow-card)] transition hover:border-brand"
        >
          {platformLogos[p.name] ? (
            <img
              src={platformLogos[p.name]}
              alt={`${p.name} logo`}
              loading="lazy"
              className="h-7 w-auto max-w-[130px] object-contain object-left"
            />
          ) : (
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
              {p.name}
            </p>
          )}

          <p className="mt-2 text-2xl font-extrabold text-secondary">{p.score}</p>
          <p className="text-sm font-semibold text-brand">{p.count}</p>
          <p className="mt-1 text-xs text-muted-foreground">{p.note}</p>
          <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-secondary">
            Bekijk bij de bron <ExternalLink className="size-3" aria-hidden />
          </span>
        </a>
      ))}
    </div>
  );
}

export function ReviewsBlock({
  limit,
  source,
  items,
  hideSummary,
}: {
  limit?: number;
  source?: Review["source"];
  items?: Review[];
  hideSummary?: boolean;
}) {
  const base = items ?? reviews;
  const filtered = source ? base.filter((r) => r.source === source) : base;
  const list = mixBySource(filtered).slice(0, limit ?? filtered.length);

  return (
    <div>
      {reviewSummary && !hideSummary ? (
        <div className="mb-8 flex flex-wrap items-center gap-4 rounded-xl border border-border bg-background p-5">
          <Stars rating={Math.round(reviewSummary.rating)} />
          <p className="font-semibold text-secondary">
            {reviewSummary.rating.toString().replace(".", ",")} gemiddeld uit {reviewSummary.count}{" "}
            reviews
          </p>
          <span className="text-sm text-muted-foreground">
            verzameld op Google, Trustoo en VerhuisOffertes
          </span>
        </div>
      ) : null}
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {list.map((review) => (
          <figure
            key={`${review.name}-${review.text.slice(0, 16)}`}
            className="flex h-full flex-col rounded-xl border border-border bg-background p-6 shadow-[var(--shadow-card)]"
          >
            <div className="flex items-center justify-between gap-2">
              <Stars rating={review.rating} />
              {review.date ? (
                <time dateTime={review.date} className="text-xs text-muted-foreground">
                  {new Date(review.date).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}
                </time>
              ) : null}
            </div>
            <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground">
              “{review.text}”
            </blockquote>
            <figcaption className="mt-4 text-sm font-semibold text-secondary">
              {review.name}
              {review.place ? <span className="text-muted-foreground"> — {review.place}</span> : null}
              {(() => {
                const url =
                  reviewPlatforms.find((p) => p.name === review.source)?.url ??
                  (review.source === "Facebook" ? "https://www.facebook.com/share/1DUztyTayT/" : undefined);
                return url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground underline-offset-2 hover:text-brand hover:underline"
                  >
                    Bekijk op {review.source} <ExternalLink className="size-3" aria-hidden />
                  </a>
                ) : (
                  <span className="mt-0.5 block text-xs font-medium text-muted-foreground">
                    via {review.source}
                  </span>
                );
              })()}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}
